import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Grid2,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Container,
  Typography,
  Paper,
  SelectChangeEvent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Coach } from "../../model/entity/Coach";
import { Gender } from "../../model/common/Gender";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import {
  CoachValidationObj,
  coachValidationObjInit,
  validateCoach,
} from "../../model/validation/CoachValidation";
import handleAxiosError from "../../components/ErrorHandler";
import { notifErr, notifOk } from "../../components/Functions";
import { Loading } from "../../components/Loading";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { CoachLeadsCourseDTO } from "../../model/entity/CoachLeadsCourseDTO";
import { Forward } from "@mui/icons-material";
import {
  deleteFetcher,
  getFetcher,
  putFetcher,
} from "../../components/Fetchers";

const EditCoach = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [coach, setCoach] = useState<Coach | null>(null);
  const [coachLeadsCourseDTOs, setCoachLeadsCourseDTOs] = useState<
    CoachLeadsCourseDTO[] | null
  >(null);
  const [coachErr, setCoachErr] = useState<CoachValidationObj>(
    coachValidationObjInit
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!coach) return;
    setCoach({ ...coach, [e.target.name]: e.target.value });
  };

  const handleGenderInputChange = (e: SelectChangeEvent) => {
    if (!coach) return;
    setCoach({ ...coach, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    const fetchData = async (url: string, callbackfn: any) => {
      try {
        const response = await getFetcher(url);
        callbackfn(response.data);
      } catch (err) {
        notifErr(handleAxiosError(err));
      }
    };

    fetchData(`${process.env.REACT_APP_API_URL}/coaches/${id}`, setCoach);
    fetchData(
      `${process.env.REACT_APP_API_URL}/coach-leads-courses/active-by-coach/${id}`,
      (data: CoachLeadsCourseDTO[]) => {
        const sortedData = data.sort(sortCoachLeadsCourses);
        setCoachLeadsCourseDTOs(sortedData);
      }
    );
  }, [id]);

  useEffect(() => {
    if (!coach) return;
    if (triedSubmit) setCoachErr(validateCoach(coach));
  }, [coach, triedSubmit]);

  const sortCoachLeadsCourses = (a: CoachLeadsCourseDTO, b: CoachLeadsCourseDTO) => {
    return a.courseDTO!.name.localeCompare(b.courseDTO!.name);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!coach) return;
    setTriedSubmit(true);

    const coachErrors = validateCoach(coach);
    setCoachErr(coachErrors);

    if (Object.values(coachErrors).some((val) => val === false)) return;

    try {
      await putFetcher(
        `${process.env.REACT_APP_API_URL}/coaches/${coach.id}`,
        coach
      );
      notifOk("Změny byly uloženy.");
      navigate(-1);
    } catch (err) {
      notifErr(handleAxiosError(err));
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleDeleteConfirm = async () => {
    if (!coach) return;
    try {
      await deleteFetcher(
        `${process.env.REACT_APP_API_URL}/coaches/${coach.id}`
      );
      notifOk("Trenér úspěšně smazán.");
      navigate(-1);
    } catch (err) {
      notifErr(handleAxiosError(err));
    } finally {
      setOpenDialog(false);
    }
  };

  if (coach === null || coachLeadsCourseDTOs === null) return <Loading />;

  const dialogMsg = (
    <div>
      Opravdu chcete odebrat trenéra
      <b> {coach.firstName + " " + coach.lastName}</b>?<br />
      Tato akce je nevratná.
    </div>
  );

  return (
    <Container
      maxWidth="md"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <ConfirmationDialog
        open={openDialog}
        setOpen={setOpenDialog}
        msg={dialogMsg}
        handleSubmit={handleDeleteConfirm}
      />
      <Paper elevation={3} sx={{ padding: "1.5rem" }}>
        <Typography variant="h5" sx={{ marginBottom: "1.5rem" }}>
          {coach.firstName + " " + coach.lastName}
        </Typography>
        <Grid2 container spacing={3} size={{ xs: 12, md: 6 }}>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Jméno"
              required
              name="firstName"
              value={coach.firstName}
              error={!coachErr.firstName}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Příjmení"
              required
              name="lastName"
              value={coach.lastName}
              error={!coachErr.lastName}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Přezdívka"
              required
              name="nickname"
              value={coach.nickname}
              error={!coachErr.nickname}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD.MM.YYYY"
                label="Datum narození"
                sx={{ width: "100%" }}
                name="birthDate"
                value={coach.birthDate === "" ? null : dayjs(coach.birthDate)}
                onChange={(date) =>
                  setCoach({
                    ...coach,
                    birthDate: date ? date.format("YYYY-MM-DD") : "",
                  })
                }
                slotProps={{
                  textField: {
                    size: "small",
                    required: true,
                    error:
                      !coachErr.birthDate &&
                      !dayjs(coach.birthDate, "YYYY-MM-DD", true).isValid(),
                  },
                  field: { clearable: true },
                }}
              />
            </LocalizationProvider>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControl required size="small" fullWidth>
              <InputLabel>Pohlaví</InputLabel>
              <Select
                label="Pohlaví"
                name="gender"
                value={coach.gender}
                error={!coachErr.gender}
                onChange={handleGenderInputChange}
              >
                <MenuItem value={Gender.MALE}>Muž</MenuItem>
                <MenuItem value={Gender.FEMALE}>Žena</MenuItem>
              </Select>
            </FormControl>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Email"
              type="email"
              required
              name="email"
              value={coach.email}
              error={!coachErr.email}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Telefon"
              type="tel"
              required
              name="phone"
              value={coach.phone}
              error={!coachErr.phone}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Adresa bydliště"
              required
              name="address"
              value={coach.address}
              error={!coachErr.address}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Rodné číslo (bez lomítka)"
              name="nationalId"
              value={coach.nationalId}
              error={!coachErr.nationalId}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Hodinová sazba (Kč)"
              type="number"
              required
              name="wage"
              value={coach.wage}
              error={!coachErr.wage}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Sazba daně (%)"
              type="number"
              required
              name="tax"
              value={coach.tax}
              error={!coachErr.tax}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Bankovní účet"
              required
              name="accountNumber"
              value={coach.accountNumber}
              error={!coachErr.accountNumber}
              onChange={handleInputChange}
            />
          </Grid2>

          {/* START SIGNED UP COURSES */}

          <Grid2
            size={{ xs: 12 }}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Typography fontWeight="bold" variant="subtitle1">
              Vedené kurzy
            </Typography>
          </Grid2>

          <Grid2
            size={{ xs: 12 }}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Table
              size="small"
              sx={{
                width: "45%",
                maxHeight: "14.5rem",
                overflowY: "auto",
                scrollbarWidth: "thin",
                display: "block",
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 100,
                  }}
                >
                  <TableCell sx={{ fontWeight: "bold", width: "100%" }}>
                    Název
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>
                    Přejít
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coachLeadsCourseDTOs.map(
                  (clc, index) =>
                    clc.courseDTO !== undefined && (
                      <TableRow key={index}>
                        <TableCell>{clc.courseDTO.name}</TableCell>
                        <TableCell sx={{ textAlign: "right" }}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              clc.courseDTO !== undefined &&
                              navigate(
                                `/overview/courses/edit/${clc.courseDTO.id}`
                              )
                            }
                          >
                            <Forward sx={{ color: "var(--gradientStart)" }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    )
                )}
              </TableBody>
            </Table>
          </Grid2>

          {/* END SIGNED UP COURSES */}

          <Grid2 container size={{ xs: 12 }} justifyContent="space-between">
            <Button
              variant="contained"
              className="deleteBtn"
              onClick={() => setOpenDialog(true)}
            >
              Smazat
            </Button>
            <div>
              <Button
                variant="outlined"
                className="cancelBtn"
                onClick={handleCancel}
                sx={{ mr: 1.5 }}
              >
                Zrušit
              </Button>
              <Button
                variant="outlined"
                className="submitBtn"
                onClick={handleSubmit}
              >
                Uložit
              </Button>
            </div>
          </Grid2>
        </Grid2>
      </Paper>
    </Container>
  );
};

export default EditCoach;
